<template>
    <div class="view-container">
        <div class="notifications-bar">
            <span class="hint">{{ $t('NotificationsStatusHint') }}</span>
            <span v-if="failedCount" class="run-error">{{ $t('NotificationsFailedCount', { count: failedCount }) }}</span>
            <span v-if="runMessage" class="run-message">{{ runMessage }}</span>
            <span v-if="runError" class="run-error">{{ runError }}</span>
        </div>
        <div class="max-grid grid-filters">
            <ejs-grid ref="grid" height="100%" width="100%" :toolbar="toolbar" :toolbarClick="toolbarClick" :selectionSettings="selectionOptions" :allowTextWrap="true" :textWrapSettings="wrapSettings" :dataBound="dataBound" :dataSource="data" :allowPaging="true" :pageSettings="pageSettings" :allowFiltering="true" :filterSettings="filterOptions" :allowSorting="true" :allowMultiSorting='true' :allowPdfExport='true' :allowCsvExport='true' :allowExcelExport='true' :showBusyIndicator="true">
                <e-columns>
                    <e-column field="createDate" :headerText="$t('CreateDate')" textAlign="Left" :customAttributes="customAttributes" type="dateTime" :format="dateTimeFormatOptions"></e-column>
                    <e-column field="statusLabel" :headerText="$t('Status')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="sentAt" :headerText="$t('SentAt')" width="150px" textAlign="Left" :customAttributes="customAttributes" type="dateTime" :format="dateTimeFormatOptions"></e-column>
                    <e-column field="typeLabel" :headerText="$t('Type')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="recipientName" :headerText="$t('Recipient')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="recipientEmail" :headerText="$t('Email')" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="body" :headerText="$t('Description')" width="400px" textAlign="Left" :customAttributes="customAttributes"></e-column>
                    <e-column field="errorText" :headerText="$t('NotificationError')" width="240px" clipMode="EllipsisWithTooltip" textAlign="Left" :customAttributes="errorAttributes"></e-column>
                </e-columns>
            </ejs-grid>
        </div>
    </div>
</template>

<script>
import { CommandColumn, ExcelExport, Filter, Page, PdfExport, Search, Sort, Toolbar } from "@syncfusion/ej2-vue-grids";
import moment from "moment";
import { getSystemNotifications, sendNotificationsNow } from "../../api/api";

// Nazwy z NotificationType (API zwraca nazwe, nie numer) na klucze tlumaczen.
const TYPE_KEYS = {
    NewTask: 'NotificationTypeNewTask',
    ClosedTask: 'NotificationTypeClosedTask',
    ResponseToTask: 'NotificationTypeResponseToTask',
    NewAnimalDidNotEat: 'NotificationTypeAnimalDidNotEat'
}

// Nazwy z NotificationSendStatusEnum na klucze tlumaczen.
const STATUS_KEYS = {
    Pending: 'NotificationStatusPending',
    Sent: 'NotificationStatusSent',
    Failed: 'NotificationStatusFailed',
    FailedPermanently: 'NotificationStatusFailedPermanently',
    SkippedStale: 'NotificationStatusSkippedStale',
    SkippedNoRecipient: 'NotificationStatusSkippedNoRecipient',
    SkippedNoSystem: 'NotificationStatusSkippedNoSystem',
    SkippedAlreadyRead: 'NotificationStatusSkippedAlreadyRead',
    SkippedSeenInApp: 'NotificationStatusSkippedSeenInApp',
    LegacyProcessed: 'NotificationStatusLegacyProcessed'
}

const FAILED_STATUSES = ['Failed', 'FailedPermanently']

export default {
    props: {
        systemName: {
            required: false,
            type: String
        },
        systemId: {
            required: false,
            type: String
        }
    },
    computed: {
        failedCount() {
            return this.data.filter(el => FAILED_STATUSES.includes(el.status)).length
        }
    },
    provide: {
        grid: [Sort, Page, Filter, Toolbar, Search, CommandColumn, PdfExport, ExcelExport],
    },
    data() {
        return {
            data: [],
            runMessage: '',
            runError: '',
            pageSettings: { pageSize: 100, pageSizes: [10, 25, 50, 100] },
            toolbar: [
                // Wysylka rusza sama kilkanascie sekund po zapisaniu powiadomienia; przycisk
                // jest do testow i do ponowienia po awarii bramki. Endpoint wymaga roli Admin.
                this.$store.getters.isAdmin
                    ? { text: this.$t('SendNow'), id: 'send-now', prefixIcon: 'e-play', align: 'Left' }
                    : null,
                { text: this.$t('ClearFilter'), id: 'clear-filters', prefixIcon: 'e-cancel', align: 'Right',},
                { text: this.$t('Excelexport'), prefixIcon: 'e-excelexport', align: 'Right', id: 'excel-export'},
                { text: this.$t('Pdfexport'), prefixIcon: 'e-pdfexport', align: 'Right', id: 'pdf-export'},
                { text: this.$t('Csvexport'), prefixIcon: 'e-csvexport', align: 'Right', id: 'csv-export'},
                'Search',
            ].filter(Boolean),
            wrapSettings: { wrapMode: "Content" },
            selectionOptions: { type: "Multiple" },
            filterOptions: {
                type: "CheckBox",
            },
            customAttributes: { class: "customcss" },
            errorAttributes: { class: ["customcss", "notification-error-cell"] },
            dateTimeFormatOptions: { format: 'yyyy-MM-dd HH:mm:ss', type: 'dateTime' },
        }
    },
    methods: {
        async getData() {
            this.$store.dispatch('set_loading', true)

            try {
                const notifications = await getSystemNotifications(this.systemId)

                this.data = notifications.map(el => ({
                    ...el,
                    // CreateDate zapisywany jest w UTC, wiec przeliczamy na strefe przegladarki -
                    // inaczej godzina wysylki rozjezdza sie o offset.
                    createDate: moment.utc(el.createDate).toDate(),
                    recipientName: el.recipientName || el.recipientEmail || '-',
                    typeLabel: TYPE_KEYS[el.type] ? this.$t(TYPE_KEYS[el.type]) : el.type,
                    statusLabel: STATUS_KEYS[el.status] ? this.$t(STATUS_KEYS[el.status]) : el.status,
                    sentAt: el.sentAt ? moment.utc(el.sentAt).toDate() : null,
                    // Kod i tresc w jednej kolumnie - kod jest tym, czego sie szuka, tresc jest kontekstem.
                    errorText: el.errorCode ? `${el.errorCode}${el.errorMessage ? ' — ' + el.errorMessage : ''}` : ''
                }))
            } finally {
                this.$store.dispatch('set_loading', false)
            }
        },
        async sendNow() {
            this.runMessage = ''
            this.runError = ''

            try {
                const result = await sendNotificationsNow()

                this.runMessage = result.message || this.$t('NotificationsSendResult', {
                    pending: result.pending,
                    sent: result.sent,
                    failed: result.failed,
                    rejected: result.rejected,
                    skipped: result.skippedStale + result.skippedBroken + result.skippedRead
                })

                await this.getData()
            } catch (e) {
                this.runError = this.$t('SendNowFailed')
            }
        },
        dataBound: function () {
            // Bez listy autoFit rozciagnalby kolumne bledu do pelnej tresci komunikatu z FCM.
            this.$refs.grid.autoFitColumns(['createDate', 'statusLabel', 'typeLabel', 'recipientName', 'recipientEmail']);
        },
        toolbarClick: function (args) {
            switch (args.item.id) {
                case "send-now":
                    this.sendNow();
                    break;
                case "pdf-export":
                    this.$refs.grid.pdfExport();
                    break;
                case "csv-export":
                    this.$refs.grid.csvExport();
                    break;
                case "excel-export":
                    this.$refs.grid.excelExport();
                    break;
                case 'clear-filters':
                    this.$refs.grid.clearFiltering();
                    break;
            }
        },
    },
    mounted: function() {
        this.getData()
    }
}
</script>

<style scoped>
/* Wysokosc razem z marginesem miesci sie w 68 px, ktore .grid-filters odejmuje od tabeli. */
.notifications-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 16px;
    min-height: 32px;
    margin-bottom: 10px;
    font-size: 13px;
}

.hint {
    color: var(--text-muted);
}

.run-message {
    color: var(--brand-text);
    font-weight: 600;
}

.run-error {
    color: var(--danger);
    font-weight: 600;
}
</style>

<style>
/* allowTextWrap na calym gridzie wylacza clipMode - bez tego 512-znakowy blad z FCM
   rozciagalby wiersz na pol ekranu. Pelna tresc jest w tooltipie. Styl nie jest scoped,
   bo komorki renderuje Syncfusion, bez atrybutu data-v tego komponentu. */
.e-grid td.notification-error-cell {
    white-space: nowrap !important;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>



// WEBPACK FOOTER //
// src/views/System/SystemNotifications.vue